import { useEffect, useMemo, useState } from "react"
import { Button } from "@/modules/shared/ui/components/button"
import { Card } from "@/modules/shared/ui/components/card"
import { apiClient } from "@/lib/api-client"
import { Check, ArrowUpRight, Loader2 } from "lucide-react"
import type { BillingPlanDto } from "@/modules/subscriptions/dtos/billing-plan.dto"
import type { CreateSubscriptionResponseDto } from "@/modules/subscriptions/dtos/subscription-response.dto"

type BillingCycle = "Monthly" | "Annual"

function redirectToPayFast(
  redirectUrl: string,
  formData: Record<string, string>,
) {
  const form = document.createElement("form")
  form.method = "POST"
  form.action = redirectUrl

  for (const [name, value] of Object.entries(formData)) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  document.body.appendChild(form)
  form.submit()
}

interface BillingPlansPageProps {
  onPlanSelect?: (plan: BillingPlanDto, isYearly: boolean) => void
}

export default function BillingPlansPage({
  onPlanSelect,
}: BillingPlansPageProps) {
  const [plans, setPlans] = useState<BillingPlanDto[]>([])
  const [isYearly, setIsYearly] = useState(false)
  const [loading, setLoading] = useState(true)
  const [startingCheckout, setStartingCheckout] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)

  const planIndex = useMemo(
    () =>
      plans.findIndex((plan) => {
        const code = plan.code?.toLowerCase()
        return code === "both" || code === "fullhire"
      }),
    [plans],
  )

  const visiblePlans = useMemo(() => {
    if (planIndex === -1) return plans

    const targetIndex = Math.floor(plans.length / 2)
    if (planIndex === targetIndex) return plans

    const reordered = [...plans]
    const [platformPlan] = reordered.splice(planIndex, 1)
    reordered.splice(targetIndex, 0, platformPlan)
    return reordered
  }, [plans, planIndex])

  useEffect(() => {
    // TODO: This is a temporary solution to ensure a plan is selected by default when plans load.
    // We should ideally have the backend indicate which plan (if any) should be pre-selected
    if (visiblePlans.length > 0 && selectedPlanId === null) {
      const centerIndex = Math.floor(visiblePlans.length / 2)
      setSelectedPlanId(String(visiblePlans[centerIndex].id))
    }
  }, [visiblePlans, selectedPlanId])

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true)
        setError(null)

        const { data } = await apiClient.get<BillingPlanDto[]>(
          "/billingplan/active",
        )
        setPlans(data)
      } catch (_e) {
        setError("Failed to load billing plans")
      } finally {
        setLoading(false)
      }
    }

    loadPlans()
  }, [])

  const handleChoose = async (plan: BillingPlanDto) => {
    if (onPlanSelect) {
      onPlanSelect(plan, isYearly)
      return
    }

    const billingCycle: BillingCycle = isYearly ? "Annual" : "Monthly"
    const discount = plan.discount ?? 0.1
    const amount = isYearly
      ? 12 * plan.monthlyPrice * (1 - discount)
      : plan.monthlyPrice
    const amountIncVat = (amount * 1.15).toFixed(2)

    try {
      setStartingCheckout(String(plan.id))
      setError(null)

      const { data } = await apiClient.post<CreateSubscriptionResponseDto>(
        "/subscription/create",
        {
          planId: String(plan.id),
          billingInterval: billingCycle,
        },
      )

      if (data.paymentRedirect) {
        const patchedFormData = {
          ...data.paymentRedirect.formData,
          amount: amountIncVat,
          recurring_amount: amountIncVat,
        }
        redirectToPayFast(data.paymentRedirect.redirectUrl, patchedFormData)
      }
    } catch (_e) {
      setError("Failed to start checkout. Please try again.")
      setStartingCheckout(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error && !visiblePlans.length) {
    return (
      <section className="relative py-24 overflow-hidden bg-background min-h-screen">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <p className="text-destructive">{error}</p>
        </div>
      </section>
    )
  }

  if (!visiblePlans.length) {
    return (
      <section className="relative py-24 overflow-hidden bg-background min-h-screen">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center text-foreground">
          <p>No billing plans available.</p>
        </div>
      </section>
    )
  }

  const popularPlanIndex = Math.floor(visiblePlans.length / 2)

  return (
    <section className="relative py-3 overflow-hidden bg-background">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center text-foreground">
          <div className="mt-0 inline-flex rounded-full bg-muted p-1">
            <Button
              onClick={() => setIsYearly(false)}
              className={`px-5 py-2 text-sm rounded-full transition ${!isYearly ? "bg-primary text-primary-foreground hover:bg-primary/80" : "bg-transparent text-muted-foreground hover:bg-muted"}`}
            >
              Monthly
            </Button>
            <Button
              onClick={() => setIsYearly(true)}
              className={`px-5 py-2 text-sm rounded-full transition ${isYearly ? "bg-primary text-primary-foreground hover:bg-primary/80" : "bg-transparent text-muted-foreground hover:bg-muted"}`}
            >
              Yearly
            </Button>
          </div>

          {error && <p className="mt-4 text-destructive text-sm">{error}</p>}
        </div>

        <div className="mx-auto mt-4 grid max-w-lg grid-cols-1 gap-3 sm:max-w-none sm:grid-cols-3 lg:mt-10 lg:gap-6">
          {visiblePlans.map((plan, index) => {
            const isPopular = index === popularPlanIndex
            const isSelected = selectedPlanId === String(plan.id)
            const isCheckingOut = startingCheckout === String(plan.id)
            const discount = plan.discount ?? 0.1
            const yearlyPrice = 12 * plan.monthlyPrice * (1 - discount)
            const yearlySavings = 12 * plan.monthlyPrice - yearlyPrice

            return (
              <Card
                key={plan.id}
                className={`relative rounded-xl border shadow-md cursor-pointer transition-all duration-200 ${isSelected
                  ? "border-primary ring-2 ring-primary/30"
                  : "border-border hover:border-muted-foreground"
                  }`}
                onClick={() => setSelectedPlanId(String(plan.id))}
              >
                {isPopular && (
                  <span className="absolute right-3 top-3 text-[10px] font-semibold text-primary lg:right-5 lg:top-5 lg:text-xs">
                    POPULAR
                  </span>
                )}

                {/* Horizontal layout on sm/md, vertical on lg */}
                <div className="flex flex-row lg:flex-col">

                  {/* Price block — left on sm/md, top on lg */}
                  <div className="flex w-[42%] flex-col items-center justify-center border-r border-border p-3 text-center lg:w-auto lg:border-r-0 lg:p-6">
                    <div className="mx-auto mb-2 hidden h-10 w-10 items-center justify-center rounded-full bg-secondary text-secondary-foreground lg:flex">
                      R
                    </div>
                    <p className="text-xs font-semibold text-foreground lg:text-lg">{plan.name}</p>
                    <p className="mt-1 text-lg font-bold text-foreground lg:mt-3 lg:text-4xl">
                      R{isYearly ? yearlyPrice.toFixed(0) : plan.monthlyPrice.toFixed(0)}
                    </p>
                    <p className="text-[10px] text-muted-foreground lg:text-sm">
                      {isYearly ? "/yr" : "/mo"} excl. VAT
                    </p>
                    {isYearly ? (
                      <p className="mt-0.5 text-[10px] font-medium text-green-600 lg:mt-1 lg:text-xs">
                        Save {(discount * 100).toFixed(0)}% (R{yearlySavings.toFixed(0)})
                      </p>
                    ) : (
                      <p className="mt-0.5 text-[10px] invisible lg:mt-1 lg:text-xs">—</p>
                    )}
                  </div>

                  {/* Features + button — right on sm/md, bottom on lg */}
                  <div className="flex w-[58%] flex-col justify-between p-3 lg:w-auto lg:p-6 lg:pt-0">
                    <ul className="space-y-1.5 text-[11px] text-muted-foreground lg:space-y-3 lg:text-sm">
                      <li className="flex items-center gap-1.5">
                        <Check className="h-3 w-3 shrink-0 text-primary lg:h-4 lg:w-4" />
                        Up to {plan.maxUsers} users
                      </li>
                      <li className="flex items-center gap-1.5">
                        <Check className="h-3 w-3 shrink-0 text-primary lg:h-4 lg:w-4" />
                        {plan.trialDays} days free trial
                      </li>
                      {plan.description && (
                        <li className="flex items-start gap-1.5">
                          <Check className="mt-0.5 h-3 w-3 shrink-0 text-primary lg:h-4 lg:w-4" />
                          <span className="line-clamp-2">{plan.description}</span>
                        </li>
                      )}
                    </ul>

                    <Button
                      className={`mt-2 w-full rounded-full flex items-center justify-center gap-1 text-[11px] h-7 lg:mt-4 lg:h-10 lg:gap-2 lg:text-sm ${isSelected
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "border border-border text-foreground hover:bg-muted hover:text-foreground"}`}
                      variant={isSelected ? "default" : "outline"}
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedPlanId(String(plan.id))
                        handleChoose(plan)
                      }}
                      disabled={!!startingCheckout}
                    >
                      {isCheckingOut ? "Redirecting..." : "Get Started"}
                      {!isCheckingOut && (
                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent text-accent-foreground lg:h-6 lg:w-6">
                          <ArrowUpRight size={10} />
                        </span>
                      )}
                    </Button>
                  </div>

                </div>
              </Card>
            )
          })}
        </div>
      </div>
    </section>
  )
}
